/**
 * Mock API Layer - Centralizzato
 *
 * Questo modulo fornisce un'interfaccia unificata per tutte le operazioni CRUD
 * della piattaforma Engine AI. Simula chiamate API con latenza realistica e
 * mantiene lo stato in memoria (con persistenza opzionale su localStorage).
 */

import {
  mockAgents,
  mockCostData,
  mockHourlyActivity,
  mockKnowledgeSourcesByAgent,
  mockToolsByAgent,
  mockWidgetsByAgent,
  mockUsers,
  mockInteractionsByAgent,
} from '../data/mockData'
import type {
  Agent,
  Policy,
  KnowledgeSource,
  Tool,
  Widget,
  User,
  Interaction,
  ConversationCost,
} from '../types'

// ============================================================================
// UTILITIES
// ============================================================================

const delay = (ms: number = 300) =>
  new Promise((resolve) => setTimeout(resolve, ms))

const STORAGE_KEYS = {
  AGENTS: 'engine_ai_agents',
  POLICIES: 'engine_ai_policies',
  KNOWLEDGE: 'engine_ai_knowledge',
  TOOLS: 'engine_ai_tools',
  WIDGETS: 'engine_ai_widgets',
  USERS: 'engine_ai_users',
  INTERACTIONS: 'engine_ai_interactions',
  ACCOUNT: 'engine_ai_account',
}

// ============================================================================
// IN-MEMORY STATE
// ============================================================================

let agents: Agent[] = [...mockAgents]
let policies: Policy[] = [
  {
    id: 'p1',
    title: 'Nessun dato sensibile',
    description:
      'Gli agenti non devono mai richiedere, memorizzare o condividere dati personali sensibili (SSN, carte di credito, password).',
    category: 'privacy' as const,
    enabled: true,
    severity: 'high' as const,
  },
  {
    id: 'p2',
    title: 'Contenuti inappropriati',
    description:
      'Blocca generazione di contenuti violenti, discriminatori o offensivi.',
    category: 'content' as const,
    enabled: true,
    severity: 'high' as const,
  },
  {
    id: 'p3',
    title: 'Consulenza legale professionale',
    description:
      'Gli agenti non possono fornire consulenza legale professionale.',
    category: 'compliance' as const,
    enabled: true,
    severity: 'high' as const,
  },
  {
    id: 'p4',
    title: 'Limite lunghezza risposta',
    description:
      'Le risposte non devono superare 2000 caratteri per mantenere la leggibilità.',
    category: 'behavior' as const,
    enabled: true,
    severity: 'medium' as const,
  },
  {
    id: 'p5',
    title: 'Verifica fonti',
    description:
      'Gli agenti devono citare le fonti quando forniscono informazioni fattuali.',
    category: 'behavior' as const,
    enabled: false,
    severity: 'medium' as const,
  },
  {
    id: 'p6',
    title: 'GDPR compliance',
    description:
      "Rispetta i principi GDPR: minimizzazione dati, diritto all'oblio, consenso esplicito.",
    category: 'compliance' as const,
    enabled: true,
    severity: 'high' as const,
  },
]

let knowledgeSources: Record<string, KnowledgeSource[]> = {
  ...mockKnowledgeSourcesByAgent,
}
let tools: Record<string, { tools: (Tool & { enabled: boolean })[] }> = {
  ...mockToolsByAgent,
}
let widgets: Record<string, { widgets: Widget[] }> = { ...mockWidgetsByAgent }
let users: User[] = [...mockUsers]
let interactions: Record<string, { interactions: Interaction[] }> = {
  ...mockInteractionsByAgent,
}

let accountProfile = {
  id: 'admin-1',
  firstName: 'Fabio',
  lastName: 'Gai',
  email: 'fabio.gai@engineai.com',
  role: 'Admin',
  avatarUrl:
    'https://media.licdn.com/dms/image/v2/D4D03AQHdjvBZrrPISg/profile-displayphoto-shrink_400_400/profile-displayphoto-shrink_400_400/0/1730625193571?e=1766620800&v=beta&t=4YCT3uKkbDoHARy0fsSpLWPAppdswN1VICI-ZXWsRyk',
}

// ============================================================================
// AGENTS (APP AI)
// ============================================================================

export const getAgents = async (): Promise<Agent[]> => {
  await delay()
  return [...agents]
}

export const getAgent = async (id: string): Promise<Agent | null> => {
  await delay()
  return agents.find((a) => a.id === id) || null
}

export const updateAgentConfig = async (
  id: string,
  config: Partial<Agent>,
): Promise<Agent> => {
  await delay(500)

  const index = agents.findIndex((a) => a.id === id)
  if (index === -1) throw new Error('Agent not found')

  agents[index] = { ...agents[index], ...config }

  // Persist to localStorage
  try {
    localStorage.setItem(STORAGE_KEYS.AGENTS, JSON.stringify(agents))
  } catch (e) {
    console.warn('Failed to persist agents to localStorage', e)
  }

  return agents[index]
}

export const saveInstructionVersion = async (
  agentId: string,
  section: 'premessa' | 'logica' | 'output',
  payload: { versionName: string; content: string },
): Promise<{
  id: string
  versionName: string
  content: string
  timestamp: string
}> => {
  await delay(400)

  const version = {
    id: `v_${Date.now()}`,
    versionName: payload.versionName,
    content: payload.content,
    timestamp: new Date().toISOString(),
  }

  // In a real app, this would be stored per agent/section
  // For now, we just return the version object
  return version
}

// ============================================================================
// POLICIES
// ============================================================================

export const getPolicies = async (): Promise<Policy[]> => {
  await delay()
  return [...policies]
}

export const updatePolicy = async (
  id: string,
  partial: Partial<Policy>,
): Promise<Policy> => {
  await delay(400)

  const index = policies.findIndex((p) => p.id === id)
  if (index === -1) throw new Error('Policy not found')

  policies[index] = { ...policies[index], ...partial }

  try {
    localStorage.setItem(STORAGE_KEYS.POLICIES, JSON.stringify(policies))
  } catch (e) {
    console.warn('Failed to persist policies', e)
  }

  return policies[index]
}

export const createPolicy = async (
  payload: Omit<Policy, 'id'>,
): Promise<Policy> => {
  await delay(500)

  const newPolicy: Policy = {
    id: `p_${Date.now()}`,
    ...payload,
  }

  policies.push(newPolicy)

  try {
    localStorage.setItem(STORAGE_KEYS.POLICIES, JSON.stringify(policies))
  } catch (e) {
    console.warn('Failed to persist policies', e)
  }

  return newPolicy
}

export const deletePolicy = async (id: string): Promise<void> => {
  await delay(400)

  const index = policies.findIndex((p) => p.id === id)
  if (index === -1) throw new Error('Policy not found')

  policies.splice(index, 1)

  try {
    localStorage.setItem(STORAGE_KEYS.POLICIES, JSON.stringify(policies))
  } catch (e) {
    console.warn('Failed to persist policies', e)
  }
}

// ============================================================================
// KNOWLEDGE SOURCES
// ============================================================================

export const getKnowledgeSources = async (
  agentId: string = 'default',
): Promise<KnowledgeSource[]> => {
  await delay()
  return [...(knowledgeSources[agentId] || knowledgeSources['default'])]
}

export const getKnowledgeSource = async (
  id: string,
  agentId: string = 'default',
): Promise<KnowledgeSource | null> => {
  await delay()
  const sources = knowledgeSources[agentId] || knowledgeSources['default']
  return sources.find((s) => s.id === id) || null
}

export const createKnowledgeSource = async (
  payload: Omit<KnowledgeSource, 'id'>,
  agentId: string = 'default',
): Promise<KnowledgeSource> => {
  await delay(600)

  const newSource: KnowledgeSource = {
    id: `ks_${Date.now()}`,
    ...payload,
  }

  if (!knowledgeSources[agentId]) {
    knowledgeSources[agentId] = []
  }

  knowledgeSources[agentId].push(newSource)

  try {
    localStorage.setItem(
      STORAGE_KEYS.KNOWLEDGE,
      JSON.stringify(knowledgeSources),
    )
  } catch (e) {
    console.warn('Failed to persist knowledge sources', e)
  }

  return newSource
}

export const updateKnowledgeSource = async (
  id: string,
  partial: Partial<KnowledgeSource>,
  agentId: string = 'default',
): Promise<KnowledgeSource> => {
  await delay(400)

  const sources = knowledgeSources[agentId] || knowledgeSources['default']
  const index = sources.findIndex((s) => s.id === id)
  if (index === -1) throw new Error('Knowledge source not found')

  sources[index] = { ...sources[index], ...partial }

  try {
    localStorage.setItem(
      STORAGE_KEYS.KNOWLEDGE,
      JSON.stringify(knowledgeSources),
    )
  } catch (e) {
    console.warn('Failed to persist knowledge sources', e)
  }

  return sources[index]
}

export const addKnowledgeFile = async (
  sourceId: string,
  fileMeta: { name: string; size: number },
  agentId: string = 'default',
): Promise<void> => {
  await delay(300)

  const sources = knowledgeSources[agentId] || knowledgeSources['default']
  const source = sources.find((s) => s.id === sourceId)
  if (!source) throw new Error('Knowledge source not found')

  // In real app, would process file and add to documents
  source.documentsCount = (source.documentsCount || 0) + 1
  source.lastUpdated = new Date().toLocaleDateString('it-IT')

  try {
    localStorage.setItem(
      STORAGE_KEYS.KNOWLEDGE,
      JSON.stringify(knowledgeSources),
    )
  } catch (e) {
    console.warn('Failed to persist knowledge sources', e)
  }
}

export const removeKnowledgeFile = async (
  sourceId: string,
  fileId: string,
  agentId: string = 'default',
): Promise<void> => {
  await delay(300)

  const sources = knowledgeSources[agentId] || knowledgeSources['default']
  const source = sources.find((s) => s.id === sourceId)
  if (!source) throw new Error('Knowledge source not found')

  source.documentsCount = Math.max(0, (source.documentsCount || 0) - 1)
  source.lastUpdated = new Date().toLocaleDateString('it-IT')

  try {
    localStorage.setItem(
      STORAGE_KEYS.KNOWLEDGE,
      JSON.stringify(knowledgeSources),
    )
  } catch (e) {
    console.warn('Failed to persist knowledge sources', e)
  }
}

// ============================================================================
// TOOLS
// ============================================================================

export const getGlobalTools = async (): Promise<
  (Tool & { enabled: boolean })[]
> => {
  await delay()
  return [...(tools['default']?.tools || [])]
}

export const getAgentTools = async (
  agentId: string,
): Promise<(Tool & { enabled: boolean })[]> => {
  await delay()
  return [...(tools[agentId]?.tools || tools['default']?.tools || [])]
}

export const updateGlobalTool = async (
  id: string,
  partial: Partial<Tool & { enabled: boolean }>,
): Promise<Tool & { enabled: boolean }> => {
  await delay(400)

  const toolList = tools['default']?.tools || []
  const index = toolList.findIndex((t) => t.id === id)
  if (index === -1) throw new Error('Tool not found')

  toolList[index] = { ...toolList[index], ...partial }

  try {
    localStorage.setItem(STORAGE_KEYS.TOOLS, JSON.stringify(tools))
  } catch (e) {
    console.warn('Failed to persist tools', e)
  }

  return toolList[index]
}

export const updateAgentTool = async (
  agentId: string,
  toolId: string,
  partial: Partial<Tool & { enabled: boolean }>,
): Promise<Tool & { enabled: boolean }> => {
  await delay(400)

  const toolList = tools[agentId]?.tools || []
  const index = toolList.findIndex((t) => t.id === toolId)
  if (index === -1) throw new Error('Tool not found')

  toolList[index] = { ...toolList[index], ...partial }

  try {
    localStorage.setItem(STORAGE_KEYS.TOOLS, JSON.stringify(tools))
  } catch (e) {
    console.warn('Failed to persist tools', e)
  }

  return toolList[index]
}

export const updateToolConfig = async (
  toolId: string,
  config: Record<string, any>,
): Promise<void> => {
  await delay(400)

  // Update tool config in global tools
  const toolList = tools['default']?.tools || []
  const tool = toolList.find((t) => t.id === toolId)
  if (tool) {
    ;(tool as any).config = config
  }

  try {
    localStorage.setItem(STORAGE_KEYS.TOOLS, JSON.stringify(tools))
  } catch (e) {
    console.warn('Failed to persist tools', e)
  }
}

// ============================================================================
// WIDGETS
// ============================================================================

export const getGlobalWidgets = async (): Promise<Widget[]> => {
  await delay()
  return [...(widgets['default']?.widgets || [])]
}

export const getAgentWidgets = async (agentId: string): Promise<Widget[]> => {
  await delay()
  return [...(widgets[agentId]?.widgets || widgets['default']?.widgets || [])]
}

export const updateGlobalWidget = async (
  id: string,
  partial: Partial<Widget>,
): Promise<Widget> => {
  await delay(400)

  const widgetList = widgets['default']?.widgets || []
  const index = widgetList.findIndex((w) => w.id === id)
  if (index === -1) throw new Error('Widget not found')

  widgetList[index] = { ...widgetList[index], ...partial }

  try {
    localStorage.setItem(STORAGE_KEYS.WIDGETS, JSON.stringify(widgets))
  } catch (e) {
    console.warn('Failed to persist widgets', e)
  }

  return widgetList[index]
}

export const updateAgentWidget = async (
  agentId: string,
  widgetId: string,
  partial: Partial<Widget>,
): Promise<Widget> => {
  await delay(400)

  const widgetList = widgets[agentId]?.widgets || []
  const index = widgetList.findIndex((w) => w.id === widgetId)
  if (index === -1) throw new Error('Widget not found')

  widgetList[index] = { ...widgetList[index], ...partial }

  try {
    localStorage.setItem(STORAGE_KEYS.WIDGETS, JSON.stringify(widgets))
  } catch (e) {
    console.warn('Failed to persist widgets', e)
  }

  return widgetList[index]
}

// ============================================================================
// USERS
// ============================================================================

export const getUsers = async (): Promise<User[]> => {
  await delay()
  return [...users]
}

export const getUser = async (id: string): Promise<User | null> => {
  await delay()
  return users.find((u) => u.id === id) || null
}

export const syncUsers = async (): Promise<User[]> => {
  await delay(1500) // Simulate longer sync operation

  // Simulate adding a new user or updating timestamps
  users = users.map((u) => ({
    ...u,
    // Could add lastSyncedAt field here
  }))

  try {
    localStorage.setItem(STORAGE_KEYS.USERS, JSON.stringify(users))
  } catch (e) {
    console.warn('Failed to persist users', e)
  }

  return [...users]
}

export const updateUser = async (
  id: string,
  partial: Partial<User>,
): Promise<User> => {
  await delay(400)

  const index = users.findIndex((u) => u.id === id)
  if (index === -1) throw new Error('User not found')

  users[index] = { ...users[index], ...partial }

  try {
    localStorage.setItem(STORAGE_KEYS.USERS, JSON.stringify(users))
  } catch (e) {
    console.warn('Failed to persist users', e)
  }

  return users[index]
}

export const deleteUser = async (id: string): Promise<void> => {
  await delay(400)

  const index = users.findIndex((u) => u.id === id)
  if (index === -1) throw new Error('User not found')

  users.splice(index, 1)

  try {
    localStorage.setItem(STORAGE_KEYS.USERS, JSON.stringify(users))
  } catch (e) {
    console.warn('Failed to persist users', e)
  }
}

// ============================================================================
// INTERACTIONS / HISTORY
// ============================================================================

export const getInteractions = async (
  agentId: string = 'default',
  filters?: { searchQuery?: string; dateRange?: string },
): Promise<Interaction[]> => {
  await delay()

  let interactionList = [
    ...(interactions[agentId]?.interactions ||
      interactions['default']?.interactions ||
      []),
  ]

  // Apply filters
  if (filters?.searchQuery) {
    const query = filters.searchQuery.toLowerCase()
    interactionList = interactionList.filter(
      (i) =>
        i.userInput.toLowerCase().includes(query) ||
        i.userLabel.toLowerCase().includes(query),
    )
  }

  if (filters?.dateRange && filters.dateRange !== 'all') {
    const now = new Date()
    interactionList = interactionList.filter((i) => {
      const interactionDate = new Date(i.timestamp)
      const hoursDiff =
        (now.getTime() - interactionDate.getTime()) / (1000 * 60 * 60)

      if (filters.dateRange === '24h') return hoursDiff <= 24
      if (filters.dateRange === '7d') return hoursDiff <= 168
      if (filters.dateRange === '30d') return hoursDiff <= 720
      return true
    })
  }

  return interactionList
}

export const getInteraction = async (
  id: string,
  agentId: string = 'default',
): Promise<Interaction | null> => {
  await delay()

  const interactionList =
    interactions[agentId]?.interactions ||
    interactions['default']?.interactions ||
    []
  return interactionList.find((i) => i.id === id) || null
}

// ============================================================================
// COSTS
// ============================================================================

export const getCostOverview = async () => {
  await delay()
  return {
    ...mockCostData,
    hourlyActivity: mockHourlyActivity,
  }
}

export const getCostByUser = async () => {
  await delay()
  return mockCostData.userLimits
}

export const getCostByAgent = async () => {
  await delay()
  return mockCostData.costPerAgent
}

export const getCostByConversation = async () => {
  await delay()
  return mockCostData.conversationCosts
}

export const updateUserCostLimit = async (
  userId: string,
  newLimit: number,
): Promise<void> => {
  await delay(400)

  const user = mockCostData.userLimits.find((u) => u.userId === userId)
  if (user) {
    user.limitEur = newLimit
  }

  // In real app, would persist this
}

// ============================================================================
// ACCOUNT
// ============================================================================

export const getAccountProfile = async () => {
  await delay()
  return { ...accountProfile }
}

export const updateAccountProfile = async (
  partial: Partial<typeof accountProfile>,
): Promise<typeof accountProfile> => {
  await delay(400)

  accountProfile = { ...accountProfile, ...partial }

  try {
    localStorage.setItem(STORAGE_KEYS.ACCOUNT, JSON.stringify(accountProfile))
  } catch (e) {
    console.warn('Failed to persist account profile', e)
  }

  return accountProfile
}

export const updateAccountPassword = async (payload: {
  currentPassword: string
  newPassword: string
}): Promise<void> => {
  await delay(500)

  // Mock validation - in real app would verify currentPassword
  if (payload.currentPassword.length < 1) {
    throw new Error('Password attuale richiesta')
  }

  if (payload.newPassword.length < 8) {
    throw new Error('La nuova password deve essere di almeno 8 caratteri')
  }

  // Success - password would be updated in backend
}

export const uploadAccountAvatar = async (fileMeta: {
  name: string
  size: number
}): Promise<string> => {
  await delay(800)

  // Validate
  if (fileMeta.size > 2 * 1024 * 1024) {
    throw new Error('File troppo grande (max 2MB)')
  }

  // Mock: generate a fake avatar URL
  const mockAvatarUrl = `https://ui-avatars.com/api/?name=${accountProfile.firstName}+${accountProfile.lastName}&size=200&background=random`

  accountProfile.avatarUrl = mockAvatarUrl

  try {
    localStorage.setItem(STORAGE_KEYS.ACCOUNT, JSON.stringify(accountProfile))
  } catch (e) {
    console.warn('Failed to persist account profile', e)
  }

  return mockAvatarUrl
}
